import { gql } from '@apollo/client';
import IconCopy from '@apollo/icons/small/IconCopy.svg';
import {
  Alert,
  Button,
  ButtonGroup,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import JsonView from '@uiw/react-json-view';
import copy from 'copy-to-clipboard';
import { parse } from 'graphql/language/parser';
import { print } from 'graphql/language/printer';
import { partition } from 'lodash';
import React, { useRef } from 'react';

import { JSONValue } from 'src/app/graph/explorerPage/helpers/types';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { ErrorBoundary } from 'src/components/common/errorBoundary/ErrorBoundary';
import { OpenInExplorer } from 'src/components/common/openInExplorer/OpenInExplorer';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { GraphqlCodeBlock } from 'src/components/graphqlCodeBlock/GraphqlCodeBlock';
import { QueryTraceInspectorTraceFragment } from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';
import {
  isOperationUnexecutable,
  unexecutableOperationReason,
} from 'src/lib/unexecutableOperationReason';

import { HeaderTable } from '../headerTable/HeaderTable';

export const queryTraceInspectorTraceFragment = gql`
  fragment QueryTraceInspectorTraceFragment on Trace {
    id
    http {
      statusCode
      requestHeaders {
        key
        value
      }
      responseHeaders {
        key
        value
      }
    }
    signature
    variables: variablesJSON {
      key
      value
    }
    operationName
    unexecutedOperationName
    unexecutedOperationBody
  }
`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  trace: QueryTraceInspectorTraceFragment;
  serviceId: string;
  schemaTag: string;
}

export const QueryTraceInspector = ({
  isOpen,
  onClose,
  trace,
  serviceId,
  schemaTag,
}: Props) => {
  const initialFocusRef = useRef<HTMLButtonElement>(null);
  const { http, signature, variables, operationName, unexecutedOperationBody } =
    trace;

  // These variables come from the variablesJSON resolver and their values are
  // already JSON (or are the empty string, for private variables sent by
  // apollo-engine-reporting).
  const [privateVariables, nonPrivateVariables] = partition(
    variables,
    ({ value }) => value === '',
  );
  const variablesObj: Record<string, JSONValue> = Object.fromEntries(
    nonPrivateVariables.map(({ key, value }) => {
      try {
        return [key, JSON.parse(value)];
      } catch (e) {
        return [key, '[invalid JSON]'];
      }
    }),
  );
  const variablesJSON = JSON.stringify(variablesObj, null, 4);
  const operationBody = signature
    ? print(parse(signature))
    : unexecutedOperationBody || '';
  const requestHeaders = http?.requestHeaders ?? [];
  const responseHeaders = http?.responseHeaders ?? [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      initialFocusRef={initialFocusRef}
    >
      <ModalOverlay />
      <ModalContent className="w-[80vw]">
        <ModalHeader>
          <ModalTitle>Trace Inspector</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="flex">
            <div className="mr-12 flex-1">
              <div>
                {operationName && isOperationUnexecutable(operationName) && (
                  <Alert status="warning" className="mb-4">
                    <p className="mb-1 font-semibold">Unexecutable Operation</p>
                    <p>{unexecutableOperationReason(operationName)}</p>
                  </Alert>
                )}
                <span className="text-lg font-semibold">Operation</span>{' '}
                {operationBody ? (
                  <>
                    <Tooltip
                      label="Copy"
                      clickedLabel="Copied"
                      placement="right"
                    >
                      <IconButton
                        aria-label="Copy"
                        variant="hidden"
                        size="xs"
                        onClick={() => copy(operationBody)}
                        icon={<IconCopy />}
                      />
                    </Tooltip>
                    <ErrorBoundary
                      // if we catch an error rendering the GraphQL codeblock due to parse failure,
                      // fall back to a simple text renderer.
                      renderError={() => (
                        <div className="mt-2 rounded border border-silver px-4 py-2 font-mono text-sm">
                          {operationBody}
                        </div>
                      )}
                    >
                      <GraphqlCodeBlock
                        className="mt-2 rounded border border-silver px-4 py-2 font-mono text-sm"
                        sdl={operationBody || 'signature unavailable'}
                      />
                    </ErrorBoundary>
                  </>
                ) : (
                  <div className="mt-3 rounded-lg bg-silver-light p-6 text-base">
                    <p className="mb-1 font-semibold">No Operation Signature</p>
                    <p>
                      By default, operation signatures are not reported for
                      invalid operations. If you are using Apollo Server, you
                      can{' '}
                      <ClickableText
                        className="text-link"
                        as="a"
                        rel="noopener noreferrer"
                        target="_blank"
                        href={DocsRouter.path('UsageReportingPluginWithHash', {
                          hash: 'sendunexecutableoperationdocuments',
                        })}
                      >
                        configure the usage reporting plugin to report the full
                        operation document and operation name
                      </ClickableText>{' '}
                      so you can view it in Apollo Studio's trace view.
                    </p>
                  </div>
                )}
              </div>
              {variablesJSON ? (
                <div className="mt-4 font-semibold">
                  <div>
                    Query Variables{' '}
                    <Tooltip
                      label="Copy"
                      clickedLabel="Copied"
                      placement="right"
                    >
                      <IconButton
                        aria-label="Copy"
                        variant="hidden"
                        size="xs"
                        onClick={() => copy(variablesJSON)}
                        icon={<IconCopy />}
                      />
                    </Tooltip>
                  </div>

                  {privateVariables.length ? (
                    <Alert status="info" className="mb-2 mt-4">
                      <p>
                        Some variables were <b>marked as private</b> by your
                        server:
                      </p>
                      <ul className="ml-4 mt-2 list-disc">
                        {privateVariables.map(({ key }) => (
                          <li key={key} className="font-mono text-xs">
                            {key}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-4">
                        To make changes, please{' '}
                        <ClickableText
                          className="font-semibold underline"
                          as="a"
                          target="_blank"
                          href={DocsRouter.path(
                            'UsageReportingPluginWithHash',
                            {
                              hash: 'sendvariablevalues',
                            },
                          )}
                        >
                          update your trace reporting configuration
                        </ClickableText>
                        .
                      </p>
                    </Alert>
                  ) : null}
                  <div
                    className="mt-2 rounded border border-silver px-4 py-2 font-mono text-sm"
                    data-chromatic="ignore"
                  >
                    {/* TODO (react-18): Replace the arrow to match the former "square with line" icon */}
                    <JsonView
                      value={variablesObj}
                      shortenTextAfterLength={18}
                      collapsed={2}
                      displayDataTypes={false}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-xs">This query had no variables.</p>
              )}
            </div>
            <div className="flex-1">
              {requestHeaders.length > 0 && (
                <HeaderTable title="Request Headers" headers={requestHeaders} />
              )}
              {responseHeaders.length > 0 && (
                <HeaderTable
                  className="mt-6"
                  title="Response Headers"
                  headers={responseHeaders}
                />
              )}
              {requestHeaders.length === 0 && responseHeaders.length === 0 && (
                <div className="mt-3 rounded-lg bg-silver-light p-6 text-base">
                  <p className="mb-1 font-semibold">
                    No Request / Response Headers
                  </p>
                  <p>
                    By default, Studio does not collect request / response
                    headers. You can customize this reporting option to your
                    needs with the{' '}
                    <span className="border border-silver bg-white px-1 font-mono text-base text-red">
                      sendHeaders
                    </span>{' '}
                    option of Apollo Server. Visit{' '}
                    <Link
                      isExternal
                      to="https://www.apollographql.com/docs/graph-manager/graph-manager-data-privacy/#http-headers"
                    >
                      Studio Data Privacy and Compliance: HTTP Headers
                    </Link>{' '}
                    in the Apollo docs for more information
                  </p>
                </div>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button
              ref={initialFocusRef}
              variant={operationBody ? 'secondary' : 'primary'}
              onClick={onClose}
            >
              Close
            </Button>
            {operationBody && (
              <OpenInExplorer
                graphId={serviceId}
                graphVariant={schemaTag}
                document={operationBody}
                variables={variablesJSON}
              />
            )}
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
